import {
  type BrowserStreamEvent,
  type EnvironmentId,
  type OrchestrationShellSnapshot,
  type ServerConfig,
  type TerminalEvent,
  ThreadId,
} from "@cmmd-forge/contracts";
import { type QueryClient } from "@tanstack/react-query";
import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import { useBrowserStateStore } from "~/browserStateStore";
import { pullRequestQueryKeys } from "~/lib/pullRequestQueryKeys";
import { selectThreadByRef, useStore } from "~/store";
import { useTerminalStateStore } from "~/terminalStateStore";
import { useComposerDraftStore } from "~/composerDraftStore";
import { readEnvironmentEventDependencies } from "./environmentEventDependencies";
import {
  applyShellEvent,
  reconcileSnapshotDerivedState,
  shouldApplyTerminalEvent,
} from "./environmentEventApplication";
import {
  mergeShellSnapshotWithRetainedThreads,
  reconcileThreadDetailSubscriptionEvictionForEnvironment,
  reconcileThreadDetailSubscriptionsForEnvironment,
} from "./threadDetailRetentionService";

export function invalidatePullRequestsForEnvironment(
  queryClient: Pick<QueryClient, "invalidateQueries">,
  environmentId: EnvironmentId,
): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: pullRequestQueryKeys.all(environmentId),
  });
}

export function createEnvironmentConnectionHandlers(
  onWelcome?: (payload: { readonly environment: ServerConfig["environment"] }) => void,
) {
  return {
    onWelcome: (
      payload: { readonly environment: ServerConfig["environment"] },
      environmentId: EnvironmentId,
    ) => {
      onWelcome?.(payload);
      const queryClient = readEnvironmentEventDependencies().readQueryClient();
      if (queryClient) {
        void invalidatePullRequestsForEnvironment(queryClient, environmentId);
      }
    },
    applyShellEvent,
    syncShellSnapshot: (snapshot: OrchestrationShellSnapshot, environmentId: EnvironmentId) => {
      const nextSnapshot = mergeShellSnapshotWithRetainedThreads(snapshot, environmentId);
      useStore.getState().syncServerShellSnapshot(nextSnapshot, environmentId);
      reconcileThreadDetailSubscriptionsForEnvironment(
        environmentId,
        nextSnapshot.threads.map((thread) => thread.id),
      );
      reconcileThreadDetailSubscriptionEvictionForEnvironment(environmentId);
      reconcileSnapshotDerivedState();
    },
    applyTerminalEvent: (event: TerminalEvent, environmentId: EnvironmentId) => {
      const threadRef = scopeThreadRef(environmentId, ThreadId.make(event.threadId));
      const serverThread = selectThreadByRef(useStore.getState(), threadRef);
      const hasDraftThread =
        useComposerDraftStore.getState().getDraftThreadByRef(threadRef) !== null;
      if (
        !shouldApplyTerminalEvent({
          serverThreadArchivedAt: serverThread?.archivedAt,
          hasDraftThread,
        })
      ) {
        return;
      }
      useTerminalStateStore.getState().applyTerminalEvent(threadRef, event);
    },
    applyBrowserEvent: (event: BrowserStreamEvent, environmentId: EnvironmentId) => {
      const threadRef = scopeThreadRef(environmentId, ThreadId.make(event.threadId));
      const serverThread = selectThreadByRef(useStore.getState(), threadRef);
      const hasDraftThread =
        useComposerDraftStore.getState().getDraftThreadByRef(threadRef) !== null;
      if (
        !shouldApplyTerminalEvent({
          serverThreadArchivedAt: serverThread?.archivedAt,
          hasDraftThread,
        })
      ) {
        return;
      }
      useBrowserStateStore.getState().applyBrowserEvent(threadRef, event);
    },
    // FINDING-14: the shell stream died with a non-transport error mid-session
    // (no caller was awaiting bootstrap). Surface it as a recoverable runtime
    // error so the UI reflects the degraded connection instead of silently
    // stalling the sidebar; the connection layer has already declined to reject
    // an unobserved bootstrap gate in this case.
    onShellSubscriptionError: (error: unknown, environmentId: EnvironmentId) => {
      readEnvironmentEventDependencies().setRuntimeError(environmentId, error);
    },
  };
}
